import { useLayoutEffect, useRef } from "react";

export const OFFICE_GRID_FALLBACK = "100px 44px minmax(118px,1fr) minmax(66px,1fr) minmax(164px,1fr) minmax(110px,1fr) minmax(84px,1fr) minmax(70px,1fr) minmax(80px,1fr) minmax(108px,1fr) minmax(110px,1fr)";

// Resolve intrinsic sizes outside the allocated grid. Measuring live expanding
// wrappers makes a previous wide allocation become the next minimum.
export function useOfficeListGeometry(rootRef) {
  const last = useRef(null);
  const fontsReady = useRef(false);
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    let disposed = false;
    const measure = (force = false) => {
      if (disposed) return;
      const rows = [...root.querySelectorAll('[data-office-grid-header], [data-list-row]')];
      const signature = rows.map(row => `${row.style.height}:${row.textContent}`).join("\n");
      if (!force && last.current?.root === root && last.current.signature === signature) return;
      const host = document.createElement("div");
      host.style.cssText = "position:fixed;left:-100000px;top:0;visibility:hidden;width:max-content;pointer-events:none";
      root.append(host);
      const widths = Array(11).fill(0);
      const probes = [];
      for (const row of rows) {
        const cells = row.hasAttribute("data-office-grid-header")
          ? [...row.querySelectorAll('[data-testid^="v2-sort-header-"]')]
          : [...row.children];
        cells.forEach((cell, index) => {
          const clone = cell.cloneNode(true);
          clone.removeAttribute("id");
          clone.style.width = "max-content";
          clone.style.maxWidth = "none";
          clone.style.minWidth = "0";
          // Identity controls reserve an arrow even at rest. Match their real
          // padding/margins, but remove the constraint responsible for ellipsis.
          clone.querySelectorAll("[data-row-name], [data-collection-name]").forEach(el => {
            el.style.maxWidth = "none";
            el.style.width = "max-content";
            el.style.flexShrink = "0";
          });
          // The live max-width constraint includes the control's padded box.
          // Its negative hover margins do not create extra space for the name.
          const control = clone.querySelector("[data-row-name]");
          if (control) control.style.margin = "0";
          host.append(clone);
          probes.push({ clone, index });
        });
      }
      // Batch writes before reads: one layout, not one forced layout per cell.
      for (const { clone, index } of probes) {
        widths[index] = Math.max(widths[index], Math.ceil(clone.getBoundingClientRect().width));
      }
      host.remove();
      if (!widths[0]) return;
      last.current = { root, signature };
      widths[0] = Math.min(260, Math.max(80, widths[0] + 2));
      widths[1] = 44;
      const tracks = widths.map((width, index) => index < 2
        ? `${width}px` : `minmax(${width}px,1fr)`).join(" ");
      const minimum = `${widths.reduce((sum, width) => sum + width, 0)}px`;
      if (root.style.getPropertyValue("--office-tracks") !== tracks) root.style.setProperty("--office-tracks", tracks);
      if (root.style.getPropertyValue("--office-min-width") !== minimum) root.style.setProperty("--office-min-width", minimum);
    };
    measure();
    // Intrinsic minima do not depend on available width; CSS distributes the
    // flexible remainder on resize/navigation without a measurement feedback loop.
    const fontsChanged = () => measure(true);
    document.fonts?.ready?.then(() => {
      if (!disposed && !fontsReady.current) {
        fontsReady.current = true;
        fontsChanged();
      }
    });
    document.fonts?.addEventListener?.("loadingdone", fontsChanged);
    return () => {
      disposed = true;
      document.fonts?.removeEventListener?.("loadingdone", fontsChanged);
    };
  });
}
